import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import flatten from 'lodash/flatten'
import xor from 'lodash/xor'
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { IconButton, Icons } from 'utoniq-ui'

import {
  Maybe,
  PreSaleItemFilterFragment,
  PreSaleLotteryApplicantFilterInputs,
  SkuUnit,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type LotteryApplicantFilterCellProps = {
  items: Maybe<PreSaleItemFilterFragment[]>
  maxChoice: number
  filters: PreSaleLotteryApplicantFilterInputs
  onFilters: (filters: PreSaleLotteryApplicantFilterInputs) => Promise<void>
}

export const LotteryApplicantFilterCell: Cell<LotteryApplicantFilterCellProps> =
  ({ items, maxChoice, filters, onFilters }) => {
    const { t } = useTranslation('consoleLotteryManagements')
    const [openTicketSelect, setOpenTicketSelect] = useState<boolean>(false)
    const [openChoiceNumberSelect, setOpenChoiceNumberSelect] =
      useState<boolean>(false)

    const tickets = useMemo<TicketSelectDrawerProps['tickets']>(() => {
      if (!items?.length) {
        return []
      }
      return flatten<TicketSelectDrawerProps['tickets'][number]>(
        items.map(item =>
          item.skuSettings?.units?.length
            ? item.skuSettings.units.map(unit => ({
                item,
                unit,
              }))
            : { item, unit: undefined }
        )
      )
    }, [items])

    const selectedItem = useMemo(() => {
      if (!items?.length || !filters.choices?.length) {
        return null
      }
      return tickets.find(item => {
        const id = [item.item.id, item.unit?.id].filter(o => !!o).join(':')
        return filters.choices?.includes(id)
      })
    }, [items?.length, filters.choices, tickets])

    return (
      <CellFrame sx={{ pt: 4 }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            size="small"
            endIcon={<Icons.DownArrow />}
            sx={{
              '&:hover, &': {
                maxWidth: 150,
                borderWidth: filters.choices?.length ? 2 : 1,
                borderColor: filters.choices?.length
                  ? 'primary.main'
                  : undefined,
                bgcolor: filters.choices?.length
                  ? 'secondary.light'
                  : undefined,
              },
            }}
            onClick={() => setOpenTicketSelect(true)}
          >
            {selectedItem ? (
              <Fragment>
                <Typography
                  variant="body2"
                  sx={{ width: 100, ...styleSets.lineCamp(1) }}
                >
                  {selectedItem.item?.title}
                </Typography>
                {filters.choices && filters.choices?.length > 1 && (
                  <Typography variant="body2">
                    +{filters.choices?.length - 1}
                  </Typography>
                )}
              </Fragment>
            ) : (
              <Typography variant="body2" color="text.hint">
                {t('ticketName', 'チケット/SKU')}
              </Typography>
            )}
          </Button>
          <Button
            variant="outlined"
            size="small"
            endIcon={<Icons.DownArrow />}
            onClick={() => setOpenChoiceNumberSelect(true)}
            sx={{
              '&:hover, &': {
                maxWidth: 150,
                borderWidth: filters.choiceNumber ? 2 : 1,
                borderColor: filters.choiceNumber ? 'primary.main' : undefined,
                bgcolor: filters.choiceNumber ? 'secondary.light' : undefined,
              },
            }}
          >
            <Typography
              variant="body2"
              color={filters.choiceNumber ? undefined : 'text.hint'}
            >
              {filters.choiceNumber
                ? t(`choice${filters.choiceNumber}`)
                : t('choice', '希望数')}
            </Typography>
          </Button>
        </Stack>
        <TicketSelectDrawer
          choices={filters.choices}
          open={openTicketSelect}
          tickets={tickets}
          onClose={() => setOpenTicketSelect(false)}
          onSaveFilter={onFilters}
        />
        <ChoiceNumberSelectDrawer
          maxChoice={maxChoice}
          choiceNumber={filters.choiceNumber}
          open={openChoiceNumberSelect}
          onClose={() => setOpenChoiceNumberSelect(false)}
          onSaveFilter={onFilters}
        />
      </CellFrame>
    )
  }

type TicketSelectDrawerProps = {
  open: boolean
  choices: PreSaleLotteryApplicantFilterInputs['choices']
  tickets: {
    item: PreSaleItemFilterFragment
    unit?: Pick<SkuUnit, 'id' | 'unitName'>
  }[]
  onClose: () => void
  onSaveFilter: LotteryApplicantFilterCellProps['onFilters']
}
const TicketSelectDrawer: FC<TicketSelectDrawerProps> = ({
  open,
  choices,
  tickets,
  onClose,
  onSaveFilter,
}) => {
  const { t } = useTranslation('consoleLotteryManagements')
  const [selectedTicket, setSelectedTicket] =
    useState<PreSaleLotteryApplicantFilterInputs['choices']>(undefined)

  useEffect(() => {
    setSelectedTicket(choices)
  }, [choices])

  const handleClose = useCallback(() => {
    setSelectedTicket(choices)
    onClose()
  }, [choices, onClose])

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: theme => theme.zIndex.modal + 3,
        // 単に props で指定するだけだと
        // default のスタイルが優先されるため
        // セレクタを用いた CSS 指定を行っている
        '& .MuiBackdrop-root': {
          // 画面幅に依らずバックドロップを画面全体にかける
          maxWidth: '100%',
        },
      }}
    >
      <Stack sx={{ bgcolor: 'background.default', maxHeight: '90dvh' }}>
        <Stack direction="row" sx={{ py: 4, px: 3 }} alignItems="center">
          <IconButton.Close size="small" onClick={handleClose} />
          <Typography variant="h6" align="center" width="100%">
            {t('ticketName', 'チケット/SKU')}
          </Typography>
          <Box width={30} />
        </Stack>
        <Divider />
        <Stack
          flex={1}
          sx={{ px: 5, overflowY: 'auto', ...styleSets.hideScrollbar() }}
        >
          {tickets.map(ticket => {
            const id = [ticket.item.id, ticket.unit?.id]
              .filter(o => !!o)
              .join(':')
            return (
              <Box key={id} sx={{ py: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedTicket?.includes(id)}
                      onClick={() => {
                        setSelectedTicket(prevState => xor(prevState, [id]))
                      }}
                    />
                  }
                  sx={{
                    p: 0,
                    flexDirection: 'row-reverse',
                    width: '100%',
                    justifyContent: 'space-between',
                    mx: 0,
                  }}
                  label={
                    <Typography variant="body2" width="100%">
                      {[ticket.item?.title, ticket.unit?.unitName]
                        .filter(o => !!o)
                        .join('/')}
                    </Typography>
                  }
                />
              </Box>
            )
          })}
        </Stack>
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          py={4}
          px={5}
          justifyContent="flex-end"
        >
          <Button
            variant="text"
            onClick={() => {
              onSaveFilter({ choices: undefined })
              onClose()
            }}
          >
            {t('clearAll', 'すべてクリア')}
          </Button>
          <Button
            onClick={() => {
              onSaveFilter({
                choices: selectedTicket?.length ? selectedTicket : undefined,
              })
              onClose()
            }}
          >
            {t('done', '完了')}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  )
}

type ChoiceNumberSelectDrawerProps = {
  open: boolean
  maxChoice: number
  choiceNumber: PreSaleLotteryApplicantFilterInputs['choiceNumber']
  onClose: () => void
  onSaveFilter: LotteryApplicantFilterCellProps['onFilters']
}
const ChoiceNumberSelectDrawer: FC<ChoiceNumberSelectDrawerProps> = ({
  open,
  maxChoice,
  choiceNumber,
  onClose,
  onSaveFilter,
}) => {
  const { t } = useTranslation('consoleLotteryManagements')
  const [selectedChoiceNumber, setSelectedChoiceNumber] =
    useState<PreSaleLotteryApplicantFilterInputs['choiceNumber']>(undefined)

  useEffect(() => {
    setSelectedChoiceNumber(choiceNumber)
  }, [choiceNumber])

  const handleClose = useCallback(() => {
    setSelectedChoiceNumber(choiceNumber)
    onClose()
  }, [choiceNumber, onClose])

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: theme => theme.zIndex.modal + 3,
        // 単に props で指定するだけだと
        // default のスタイルが優先されるため
        // セレクタを用いた CSS 指定を行っている
        '& .MuiBackdrop-root': {
          // 画面幅に依らずバックドロップを画面全体にかける
          maxWidth: '100%',
        },
      }}
    >
      <Stack sx={{ bgcolor: 'background.default' }}>
        <Stack
          direction="row"
          sx={{ py: 4, px: 3, overflowY: 'auto', ...styleSets.hideScrollbar() }}
          alignItems="center"
        >
          <IconButton.Close size="small" onClick={handleClose} />
          <Typography variant="h6" align="center" width="100%">
            {t('choice', '希望数')}
          </Typography>
          <Box width={30} />
        </Stack>
        <Divider />
        <Stack px={5}>
          {Array.from(Array(maxChoice).keys()).map(index => (
            <Box key={index}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectedChoiceNumber === index + 1}
                    onClick={() =>
                      setSelectedChoiceNumber(prevState =>
                        prevState === index + 1 ? undefined : index + 1
                      )
                    }
                  />
                }
                sx={{
                  p: 0,
                  flexDirection: 'row-reverse',
                  width: '100%',
                  justifyContent: 'space-between',
                  mx: 0,
                }}
                label={
                  <Typography variant="body2" width="100%">
                    {t('choiceText', { number: index + 1 })}
                  </Typography>
                }
              />
            </Box>
          ))}
        </Stack>
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          py={4}
          px={5}
          justifyContent="flex-end"
        >
          <Button
            variant="text"
            onClick={() => {
              onSaveFilter({ choiceNumber: undefined })
              onClose()
            }}
          >
            {t('clearAll', 'すべてクリア')}
          </Button>
          <Button
            onClick={() => {
              onSaveFilter({ choiceNumber: selectedChoiceNumber })
              onClose()
            }}
          >
            {t('done', '完了')}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  )
}
